/* ==========================================================================
   Fine Threads — Spin-to-win wheel (Part 1: UI + spin + email gate +
   one-spin-per-email + customer record). No real discount codes yet.
   ========================================================================== */
(() => {
  const SEGMENTS = [
    { label: ['2.5% OFF'], type: 'discount', value: 2.5 },
    { label: ['BETTER LUCK', 'NEXT DROP'], type: 'none' },
    { label: ['5% OFF'], type: 'discount', value: 5 },
    { label: ['2.5% OFF'], type: 'discount', value: 2.5 },
    { label: ['10% OFF'], type: 'discount', value: 10 },
    { label: ['BETTER LUCK', 'NEXT DROP'], type: 'none' },
    { label: ['5% OFF'], type: 'discount', value: 5 },
    { label: ['2.5% OFF'], type: 'discount', value: 2.5 },
  ];
  // Brand palette — all four colours, cycled so neighbours differ.
  const COLORS = ['#2C2C2C', '#2B3A4A', '#5C4A3A', '#FAF7F0', '#2C2C2C', '#2B3A4A', '#5C4A3A', '#FAF7F0'];
  const INK_ON = (hex) => (hex.toUpperCase() === '#FAF7F0' ? '#2C2C2C' : '#FAF7F0');
  const R = 145, CX = 150, CY = 150, SEG = 360 / SEGMENTS.length;
  const STORE_KEY = 'ft_spin_emails';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const pt = (deg, radius) => {
    const a = (deg * Math.PI) / 180;
    return [CX + radius * Math.sin(a), CY - radius * Math.cos(a)];
  };

  class SpinWheel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.segments = SEGMENTS;
      this.rotation = 0;
      this.spinning = false;
      this.done = false;

      this.wheelEl = this.querySelector('[data-wheel]');
      this.emailInput = this.querySelector('[data-spin-email]');
      this.spinBtn = this.querySelector('[data-spin]');
      this.messageEl = this.querySelector('[data-spin-message]');
      this.resultEl = this.querySelector('[data-spin-result]');
      this.customerForm = this.querySelector('[data-customer-form]');
      this.codeEl = this.querySelector('[data-spin-code]');
      this.codeValueEl = this.querySelector('[data-spin-code-value]');
      this.codeCopyEl = this.querySelector('[data-spin-code-copy]');
      // When set, the spin is decided + the code minted server-side (secure).
      // When blank, falls back to the client-side demo (no real codes).
      this.endpoint = (this.dataset.endpoint || '').trim();
      if (this.codeCopyEl) {
        this.codeCopyEl.addEventListener('click', () => {
          const v = this.codeValueEl ? this.codeValueEl.textContent : '';
          if (v && navigator.clipboard) navigator.clipboard.writeText(v).then(() => { this.codeCopyEl.textContent = 'Copied'; }).catch(() => {});
        });
      }

      this.buildWheel();
      this.emailInput.addEventListener('input', () => this.validate());
      this.emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.spin(); }
      });
      this.spinBtn.addEventListener('click', () => this.spin());
      this.validate();
    }

    /* ---- Step 1: build the visual wheel (SVG) ---- */
    buildWheel() {
      const svgns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgns, 'svg');
      svg.setAttribute('viewBox', '0 0 300 300');
      svg.setAttribute('class', 'ft-spin__svg');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'Prize wheel');

      const rot = document.createElementNS(svgns, 'g');
      rot.setAttribute('data-rotor', '');
      rot.setAttribute('class', 'ft-spin__rotor');

      this.segments.forEach((seg, i) => {
        const start = i * SEG, end = start + SEG, mid = start + SEG / 2;
        const [x1, y1] = pt(start, R);
        const [x2, y2] = pt(end, R);
        const wedge = document.createElementNS(svgns, 'path');
        wedge.setAttribute('d', `M${CX} ${CY} L${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`);
        wedge.setAttribute('fill', COLORS[i]);
        wedge.setAttribute('stroke', '#2C2C2C');
        wedge.setAttribute('stroke-width', '1');
        rot.appendChild(wedge);

        const [lx, ly] = pt(mid, R * 0.62);
        const flip = mid > 90 && mid < 270;
        const label = document.createElementNS(svgns, 'text');
        label.setAttribute('x', lx.toFixed(2));
        label.setAttribute('y', ly.toFixed(2));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('dominant-baseline', 'middle');
        label.setAttribute('transform', `rotate(${(flip ? mid + 180 : mid).toFixed(2)} ${lx.toFixed(2)} ${ly.toFixed(2)})`);
        label.setAttribute('fill', INK_ON(COLORS[i]));
        label.setAttribute('class', 'ft-spin__label');
        seg.label.forEach((line, li) => {
          const tspan = document.createElementNS(svgns, 'tspan');
          tspan.setAttribute('x', lx.toFixed(2));
          tspan.setAttribute('dy', li === 0 ? (seg.label.length > 1 ? '-0.4em' : '0') : '1.05em');
          tspan.textContent = line;
          label.appendChild(tspan);
        });
        rot.appendChild(label);
      });

      const hub = document.createElementNS(svgns, 'circle');
      hub.setAttribute('cx', CX); hub.setAttribute('cy', CY); hub.setAttribute('r', '22');
      hub.setAttribute('fill', '#FAF7F0'); hub.setAttribute('stroke', '#2C2C2C'); hub.setAttribute('stroke-width', '2');

      const ring = document.createElementNS(svgns, 'circle');
      ring.setAttribute('cx', CX); ring.setAttribute('cy', CY); ring.setAttribute('r', R);
      ring.setAttribute('fill', 'none'); ring.setAttribute('stroke', '#2C2C2C'); ring.setAttribute('stroke-width', '3');

      svg.appendChild(rot);
      svg.appendChild(ring);
      svg.appendChild(hub);
      this.wheelEl.appendChild(svg);
      this.rotor = rot;
    }

    /* ---- Step 3: email gate ---- */
    isValidEmail(v) { return EMAIL_RE.test(v); }
    validate() {
      const ok = this.isValidEmail(this.emailInput.value.trim());
      this.spinBtn.disabled = !ok || this.spinning || this.done;
      if (this.messageEl.dataset.sticky !== 'true') this.setMessage('');
    }
    setMessage(text, kind) {
      this.messageEl.textContent = text || '';
      this.messageEl.className = 'ft-spin__message' + (kind ? ' ft-spin__message--' + kind : '');
      this.messageEl.dataset.sticky = text ? 'true' : 'false';
    }

    /* ---- Step 4: one-spin-per-email (per browser via localStorage) ---- */
    spunEmails() {
      try { return JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); } catch (e) { return []; }
    }
    hasSpun(email) { return this.spunEmails().includes(email); }
    markSpun(email) {
      try {
        const s = this.spunEmails();
        if (!s.includes(email)) { s.push(email); localStorage.setItem(STORE_KEY, JSON.stringify(s)); }
      } catch (e) { /* storage blocked — non-fatal */ }
    }

    /* ---- Step 2: weighted-random selection (uniform over the 8 wedges → the
       segment counts produce the intended odds: 2.5%=3/8, 5%=2/8, 10%=1/8,
       no-prize=2/8). Every wedge is reachable; none excluded. ---- */
    pickIndex() { return Math.floor(Math.random() * this.segments.length); }

    async spin() {
      const email = this.emailInput.value.trim().toLowerCase();
      if (!this.isValidEmail(email)) { this.setMessage('Please enter a valid email address.', 'error'); return; }
      if (this.spinning || this.done) return;
      if (this.hasSpun(email)) {
        this.setMessage('This email has already used its spin — one spin per customer. Thanks for playing.', 'error');
        return;
      }
      this.spinning = true;
      this.emailInput.disabled = true;
      this.spinBtn.disabled = true;
      this.setMessage('');

      if (this.endpoint) {
        // SECURE path: the server decides the result and mints the code.
        try {
          const resp = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await resp.json();
          if (data && data.alreadySpun) {
            this.markSpun(email);
            this.spinning = false; this.done = true;
            this.setMessage('This email has already used its spin — one spin per customer. Thanks for playing.', 'error');
            return;
          }
          if (!data || data.ok === false || !data.result) throw new Error('bad-response');
          const idx = this.indexForResult(data.result);
          this.animateTo(idx, () => this.finishServer(data, email));
        } catch (e) {
          this.spinning = false;
          this.emailInput.disabled = false;
          this.setMessage('Something went wrong with your spin — please try again.', 'error');
          this.validate();
        }
        return;
      }

      // FALLBACK (no endpoint configured): Part-1 client-side demo, no real codes.
      const idx = this.pickIndex();
      this.animateTo(idx, () => this.finish(idx, email));
    }

    indexForResult(result) {
      const want = (seg) => (result === 'none' ? seg.type === 'none' : seg.type === 'discount' && seg.value + '%' === result);
      const matches = this.segments.map((s, i) => (want(s) ? i : -1)).filter((i) => i >= 0);
      return matches.length ? matches[Math.floor(Math.random() * matches.length)] : 0;
    }

    finishServer(data, email) {
      this.markSpun(email); // server is the authority; keep the local guard too
      this.spinning = false;
      this.done = true;
      if (data.result !== 'none' && data.code) {
        this.setMessage(`You won ${data.result} off — here's your code:`, 'win');
        this.showCode(data.code);
      } else {
        this.setMessage('Better luck next drop.', 'none');
      }
      if (this.resultEl) this.resultEl.textContent = data.result !== 'none' ? `${data.result} OFF` : '—';
    }

    showCode(code) {
      if (!this.codeEl) return;
      if (this.codeValueEl) this.codeValueEl.textContent = code;
      this.codeEl.hidden = false;
    }

    animateTo(idx, done) {
      const midAngle = idx * SEG + SEG / 2;            // wedge centre, clockwise from top
      const jitter = (Math.random() - 0.5) * (SEG - 10); // stay inside the wedge
      let delta = (-(midAngle + this.rotation) - jitter) % 360;
      if (delta > 0) delta -= 360;                      // always rotate forward (clockwise visual)
      const target = this.rotation + 360 * 5 + delta;   // 5 full turns + align
      this.rotation = target;
      this.rotor.style.transform = `rotate(${target}deg)`;
      let called = false;
      const finish = () => { if (called) return; called = true; done(); };
      this.rotor.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 4600); // fallback if transitionend doesn't fire
    }

    finish(idx, email) {
      const seg = this.segments[idx];
      this.markSpun(email);          // Step 4: record locally
      this.createCustomer(email);    // Step 5: create Shopify customer record
      this.spinning = false;
      this.done = true;
      if (seg.type === 'discount') {
        this.setMessage(`You won ${seg.value}% off. Your code will follow shortly.`, 'win');
      } else {
        this.setMessage('Better luck next drop.', 'none');
      }
      if (this.resultEl) this.resultEl.textContent = seg.type === 'discount' ? `${seg.value}% OFF` : '—';
    }

    /* ---- Step 5: create a Shopify customer record (email only, no password)
       via Shopify's native customer form. Submitted as a real form POST into a
       hidden iframe so it carries the browser session/cookies (a bare fetch is
       rejected by Shopify's bot protection) without navigating away from the
       modal. ---- */
    createCustomer(email) {
      if (!this.customerForm) return;
      const field = this.customerForm.querySelector('[data-customer-email]');
      if (field) field.value = email;
      let sink = document.getElementById('ft-spin-sink');
      if (!sink) {
        sink = document.createElement('iframe');
        sink.id = 'ft-spin-sink';
        sink.name = 'ft-spin-sink';
        sink.setAttribute('aria-hidden', 'true');
        sink.setAttribute('tabindex', '-1');
        sink.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;border:0;';
        document.body.appendChild(sink);
      }
      this.customerForm.setAttribute('target', 'ft-spin-sink');
      try { this.customerForm.submit(); } catch (e) { /* non-fatal */ }
    }
  }

  if (!customElements.get('spin-wheel')) customElements.define('spin-wheel', SpinWheel);
})();
